// PageFlow's own text editor: one editable region at a time, a floating toolbar, clean paste.
// Modes: "rich" (paragraphs, headings, lists, quotes, alignment), "inline" (one line with bold,
// italic and links: headings, captions) and "plain" (text only: button labels).
// A site can plug another editor for the rich regions with the `textEditor` option.
import { hFor, clamp } from '../core/dom.js';
import { safeUrl, sanitizeTree } from '../core/sanitize.js';
import { uiIcons } from '../ui/icons.js';

const RICH_ALLOWED = new Set([
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'del', 'ins', 'sub', 'sup', 'small', 'mark', 'code', 'a',
    'ul', 'ol', 'li', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'table', 'thead', 'tbody', 'tfoot', 'tr', 'th',
    'td', 'caption', 'figure', 'figcaption', 'img', 'hr', 'span', 'abbr', 'q', 'cite', 'dl', 'dt', 'dd',
]);
const INLINE_ALLOWED = new Set(['br', 'strong', 'b', 'em', 'i', 'u', 's', 'a', 'sub', 'sup', 'small', 'mark', 'code', 'span']);
const KEPT_ATTRIBUTES = { a: ['href', 'target', 'rel', 'title'], img: ['src', 'alt', 'width', 'height'], td: ['colspan', 'rowspan'], th: ['colspan', 'rowspan', 'scope'], ol: ['start', 'reversed', 'type'], abbr: ['title'] };
const BLOCK_TAGS = new Set(['p', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'li', 'td', 'th', 'figcaption', 'div', 'summary']);
const ALIGN_CLASS = /^text-(?:start|center|end|justify)$/;

/** Keeps the tags a region accepts; other elements are unwrapped, unsafe ones removed. */
export function cleanPastedHtml(html, mode, doc, { keepDataImages = false } = {}) {
    const template = doc.createElement('template');
    template.innerHTML = String(html || '').replace(/<!--\[if[\s\S]*?endif\]-->/gi, '');
    sanitizeTree(template.content);
    const allowed = mode === 'rich' ? RICH_ALLOWED : INLINE_ALLOWED;
    const walk = node => {
        for (const child of Array.from(node.childNodes)) {
            if (child.nodeType === 8) {
                child.remove();
                continue;
            }
            if (child.nodeType !== 1) continue;
            walk(child);
            let tag = child.localName;
            if (tag === 'div' && mode === 'rich') {
                const p = doc.createElement('p');
                p.append(...child.childNodes);
                child.replaceWith(p);
                continue;
            }
            if (/^h1$/.test(tag) && mode === 'rich') {
                const h2 = doc.createElement('h2');
                h2.append(...child.childNodes);
                child.replaceWith(h2);
                tag = 'h2';
                continue;
            }
            if (!allowed.has(tag)) {
                child.replaceWith(...child.childNodes);
                continue;
            }
            // A picture pasted as base64 would weigh megabytes in the page: it is sent as a file instead.
            if (tag === 'img' && !keepDataImages && /^\s*data:/i.test(child.getAttribute('src') || '')) {
                child.remove();
                continue;
            }
            const keep = KEPT_ATTRIBUTES[tag] || [];
            for (const attr of Array.from(child.attributes)) if (!keep.includes(attr.name)) child.removeAttribute(attr.name);
            if (tag === 'span' && !child.attributes.length) child.replaceWith(...child.childNodes);
            if (tag === 'b' || tag === 'i') {
                const replacement = doc.createElement(tag === 'b' ? 'strong' : 'em');
                replacement.append(...child.childNodes);
                child.replaceWith(replacement);
            }
        }
    };
    walk(template.content);
    return template.innerHTML;
}

/** Tidies a region after editing: <b>/<i> to <strong>/<em>, no stray styles, top-level paragraphs. */
export function normalizeRegion(el, mode) {
    const doc = el.ownerDocument;
    el.querySelectorAll('b, i').forEach(node => {
        const replacement = doc.createElement(node.localName === 'b' ? 'strong' : 'em');
        for (const attr of Array.from(node.attributes)) replacement.setAttribute(attr.name, attr.value);
        replacement.append(...node.childNodes);
        node.replaceWith(replacement);
    });
    el.querySelectorAll('span').forEach(span => {
        span.removeAttribute('style');
        if (!span.attributes.length) span.replaceWith(...span.childNodes);
    });
    el.querySelectorAll('font').forEach(font => font.replaceWith(...font.childNodes));
    el.querySelectorAll('[style]').forEach(node => {
        // execCommand sometimes leaves computed styles behind; alignment uses classes instead.
        node.style.removeProperty('font-size');
        node.style.removeProperty('font-family');
        node.style.removeProperty('line-height');
        node.style.removeProperty('background-color');
        node.style.removeProperty('color');
        node.style.removeProperty('text-align');
        if (!node.getAttribute('style')) node.removeAttribute('style');
    });
    el.querySelectorAll('strong:empty, em:empty, u:empty, s:empty, a:empty').forEach(node => node.remove());
    if (mode === 'rich') {
        // Text typed straight into the region goes into a paragraph.
        let paragraph = null;
        for (const node of Array.from(el.childNodes)) {
            const inline = node.nodeType === 3 || (node.nodeType === 1 && !BLOCK_TAGS.has(node.localName) && !['ul', 'ol', 'table', 'figure', 'hr', 'dl'].includes(node.localName));
            if (node.nodeType === 3 && !node.textContent.trim()) {
                if (paragraph) paragraph.append(node);
                continue;
            }
            if (inline) {
                if (!paragraph) {
                    paragraph = doc.createElement('p');
                    node.before(paragraph);
                }
                paragraph.append(node);
            } else {
                paragraph = null;
            }
        }
        el.querySelectorAll(':scope > div').forEach(div => {
            const p = doc.createElement('p');
            p.append(...div.childNodes);
            div.replaceWith(p);
        });
    } else {
        el.querySelectorAll('p, div').forEach(node => node.replaceWith(...node.childNodes));
        if (mode === 'plain') el.textContent = el.textContent.replace(/\s+/g, ' ').trim();
    }
    // A lone <br> left by the browser in an emptied element.
    if (el.innerHTML === '<br>' || el.innerHTML === '<p><br></p>') el.innerHTML = '';
}

export class TextEditing {
    constructor(editor) {
        this.editor = editor;
        this.active = null;
        this.mode = null;
        this.adapter = null;
        this.savedRange = null;
        const { doc, ui } = editor.canvas;
        this.doc = doc;
        this.h = hFor(doc);
        this.toolbar = this.h('div', { class: 'pf-ui-rte', role: 'toolbar', 'aria-label': editor.t('rte.toolbar') });
        this.linkBox = this.buildLinkBox();
        ui.append(this.toolbar, this.linkBox);
        this.buildToolbar();
        this.onSelection = () => this.refreshToolbar();
        this.onKeydown = event => this.keydown(event);
        this.onPaste = event => this.paste(event);
        this.onInput = () => editor.changed();
    }

    svg(name) {
        const span = this.doc.createElement('span');
        span.className = 'pf-ui-icon';
        span.innerHTML = `<svg viewBox="0 0 256 256" fill="currentColor" aria-hidden="true">${uiIcons[name] || ''}</svg>`;
        return span;
    }

    button(name, iconName, title, run, modes = ['rich', 'inline']) {
        const btn = this.h('button', { type: 'button', class: 'pf-ui-btn', title, 'aria-label': title, dataset: { command: name, modes: modes.join(' ') } });
        btn.append(this.svg(iconName));
        // pointerdown keeps the focus (and the selection) in the text.
        btn.addEventListener('pointerdown', event => event.preventDefault());
        btn.addEventListener('click', event => {
            event.preventDefault();
            event.stopPropagation();
            run();
            this.editor.changed();
            this.refreshToolbar();
        });
        return btn;
    }

    buildToolbar() {
        const t = this.editor.t;
        const exec = (command, value = null) => () => this.doc.execCommand(command, false, value);
        this.formatSelect = this.h('select', { class: 'pf-ui-select-format', title: t('rte.format'), 'aria-label': t('rte.format'), dataset: { modes: 'rich' } },
            [['p', t('rte.paragraph')], ['p.lead', t('rte.lead')], ['p.small', t('rte.small')], ['h2', t('rte.heading', { n: 2 })], ['h3', t('rte.heading', { n: 3 })], ['h4', t('rte.heading', { n: 4 })], ['h5', t('rte.heading', { n: 5 })], ['blockquote', t('rte.quote')], ['pre', t('rte.code')]]
                .map(([value, label]) => this.h('option', { value }, label)));
        this.formatSelect.addEventListener('change', () => {
            this.restoreRange();
            const [tag, variant] = this.formatSelect.value.split('.');
            this.doc.execCommand('formatBlock', false, `<${tag}>`);
            // "Lead" and "small" are paragraphs with a Bootstrap class.
            this.selectedBlocks('p').forEach(block => {
                block.classList.remove('lead', 'small');
                if (variant) block.classList.add(variant);
                if (!block.getAttribute('class')) block.removeAttribute('class');
            });
            this.editor.changed();
            this.refreshToolbar();
        });
        this.tableSelect = this.h('select', { class: 'pf-ui-select-format pf-ui-select-table', title: t('rte.table'), 'aria-label': t('rte.table'), dataset: { modes: 'rich' } },
            [['', t('rte.table')], ['rowAbove', t('rte.rowAbove')], ['rowBelow', t('rte.rowBelow')], ['colLeft', t('rte.colLeft')], ['colRight', t('rte.colRight')], ['deleteRow', t('rte.deleteRow')], ['deleteCol', t('rte.deleteCol')], ['deleteTable', t('rte.deleteTable')]]
                .map(([value, label]) => this.h('option', { value }, label)));
        this.tableSelect.addEventListener('pointerdown', () => this.saveRange());
        this.tableSelect.addEventListener('change', () => {
            const action = this.tableSelect.value;
            this.tableSelect.value = '';
            this.restoreRange();
            if (action) this.tableAction(action);
            this.editor.changed();
            this.refreshToolbar();
        });
        this.formatSelect.addEventListener('pointerdown', () => this.saveRange());

        const separator = modes => this.h('span', { class: 'pf-ui-sep', dataset: { modes } });
        this.toolbar.append(
            this.formatSelect,
            separator('rich'),
            this.button('bold', 'text-b', t('rte.bold'), exec('bold')),
            this.button('italic', 'text-italic', t('rte.italic'), exec('italic')),
            this.button('underline', 'text-underline', t('rte.underline'), exec('underline')),
            this.button('strikeThrough', 'text-strikethrough', t('rte.strike'), exec('strikeThrough'), ['rich']),
            separator('rich inline'),
            this.button('link', 'link', t('rte.link'), () => this.openLink()),
            separator('rich'),
            this.button('insertUnorderedList', 'list-bullets', t('rte.bulletList'), exec('insertUnorderedList'), ['rich']),
            this.button('insertOrderedList', 'list-numbers', t('rte.numberedList'), exec('insertOrderedList'), ['rich']),
            separator('rich'),
            this.button('align-start', 'text-align-left', t('rte.alignLeft'), () => this.align('text-start'), ['rich']),
            this.button('align-center', 'text-align-center', t('rte.alignCenter'), () => this.align('text-center'), ['rich']),
            this.button('align-end', 'text-align-right', t('rte.alignRight'), () => this.align('text-end'), ['rich']),
            this.button('align-justify', 'text-align-justify', t('rte.justify'), () => this.align('text-justify'), ['rich']),
            this.tableSelect,
            separator('rich inline'),
            this.button('clear', 'eraser', t('rte.clear'), () => {
                this.doc.execCommand('removeFormat');
                this.doc.execCommand('unlink');
            }),
        );
    }

    buildLinkBox() {
        const t = this.editor.t;
        const h = this.h;
        this.linkInput = h('input', { type: 'text', class: 'pf-ui-input', placeholder: 'https://', 'aria-label': t('field.url'), spellcheck: 'false' });
        this.linkNewTab = h('input', { type: 'checkbox', class: 'pf-ui-check' });
        const box = h('div', { class: 'pf-ui-link', role: 'dialog', 'aria-label': t('rte.link') },
            this.linkInput,
            h('label', { class: 'pf-ui-check-label' }, this.linkNewTab, ' ', t('field.newTab')),
            h('div', { class: 'pf-ui-link-actions' },
                h('button', { type: 'button', class: 'pf-ui-text-btn pf-ui-primary', onclick: () => this.applyLink() }, t('action.apply')),
                h('button', { type: 'button', class: 'pf-ui-text-btn', onclick: () => this.removeLink() }, t('rte.unlink')),
                h('button', { type: 'button', class: 'pf-ui-text-btn', onclick: () => this.closeLink(true) }, t('action.cancel'))));
        this.linkInput.addEventListener('keydown', event => {
            if (event.key === 'Enter') {
                event.preventDefault();
                this.applyLink();
            } else if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                this.closeLink(true);
            }
        });
        box.addEventListener('pointerdown', event => event.stopPropagation());
        return box;
    }

    /** Starts editing a region; (x, y) places the caret where the user clicked. */
    activate(el, point = null) {
        if (this.active === el) return;
        this.deactivate();
        const mode = el.getAttribute('data-pf-edit') || 'rich';
        this.active = el;
        this.mode = mode;
        this.before = el.innerHTML;
        const custom = this.editor.options.textEditor;
        if (mode === 'rich' && typeof custom === 'function') {
            this.adapter = custom(el, { document: this.doc, window: this.doc.defaultView, editor: this.editor, mode }) || null;
            el.addEventListener('input', this.onInput);
            this.editor.overlay.schedule();
            return;
        }
        el.setAttribute('contenteditable', mode === 'plain' ? 'plaintext-only' : 'true');
        if (mode === 'plain' && el.contentEditable !== 'plaintext-only') el.setAttribute('contenteditable', 'true');
        el.setAttribute('spellcheck', 'true');
        el.addEventListener('keydown', this.onKeydown);
        el.addEventListener('paste', this.onPaste);
        el.addEventListener('input', this.onInput);
        this.doc.addEventListener('selectionchange', this.onSelection);
        el.focus({ preventScroll: true });
        this.placeCaret(el, point);
        this.toolbar.dataset.mode = mode;
        this.toolbar.querySelectorAll('[data-modes]').forEach(node => {
            node.style.display = node.dataset.modes.split(' ').includes(mode) ? '' : 'none';
        });
        this.refreshToolbar();
        this.editor.overlay.schedule();
        this.editor.app.historyChanged();
    }

    placeCaret(el, point) {
        const sel = this.doc.getSelection();
        let range = null;
        if (point) {
            if (this.doc.caretPositionFromPoint) {
                const pos = this.doc.caretPositionFromPoint(point.x, point.y);
                if (pos && el.contains(pos.offsetNode)) {
                    range = this.doc.createRange();
                    range.setStart(pos.offsetNode, pos.offset);
                }
            } else if (this.doc.caretRangeFromPoint) {
                const r = this.doc.caretRangeFromPoint(point.x, point.y);
                if (r && el.contains(r.startContainer)) range = r;
            }
        }
        if (!range) {
            range = this.doc.createRange();
            range.selectNodeContents(el);
            range.collapse(false);
        }
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
    }

    deactivate() {
        const el = this.active;
        if (!el) return;
        this.closeLink(false);
        el.removeEventListener('keydown', this.onKeydown);
        el.removeEventListener('paste', this.onPaste);
        el.removeEventListener('input', this.onInput);
        this.doc.removeEventListener('selectionchange', this.onSelection);
        if (this.adapter) {
            const adapter = this.adapter;
            this.adapter = null;
            const html = typeof adapter.getHTML === 'function' ? adapter.getHTML() : el.innerHTML;
            if (typeof adapter.destroy === 'function') adapter.destroy();
            const template = this.doc.createElement('template');
            template.innerHTML = html;
            el.replaceChildren(sanitizeTree(template.content));
        }
        el.removeAttribute('contenteditable');
        el.removeAttribute('spellcheck');
        normalizeRegion(el, this.mode);
        this.toolbar.style.display = 'none';
        const changed = el.innerHTML !== this.before;
        this.active = null;
        this.mode = null;
        const sel = this.doc.getSelection();
        if (sel && el.contains(sel.anchorNode)) sel.removeAllRanges();
        if (changed) this.editor.commit();
        this.editor.overlay.schedule();
        this.editor.app.historyChanged();
    }

    keydown(event) {
        const mod = event.ctrlKey || event.metaKey;
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            this.deactivate();
            return;
        }
        if (event.key === 'Enter' && this.mode !== 'rich') {
            event.preventDefault();
            if (this.mode === 'inline' && event.shiftKey) this.doc.execCommand('insertLineBreak');
            return;
        }
        if (mod && event.key.toLowerCase() === 'k' && this.mode !== 'plain') {
            event.preventDefault();
            this.openLink();
            return;
        }
        if (event.key === 'Tab' && this.mode === 'rich' && this.closestIn('li')) {
            event.preventDefault();
            this.doc.execCommand(event.shiftKey ? 'outdent' : 'indent');
        }
        // Undo inside the text is the browser's own; the editor's history takes over once editing ends.
        event.stopPropagation();
    }

    paste(event) {
        const data = event.clipboardData;
        if (!data) return;
        event.preventDefault();
        const html = data.getData('text/html');
        const text = data.getData('text/plain');
        const pictures = Array.from(data.files || []).filter(file => /^image\//.test(file.type));
        // A picture alone (a screenshot, a copied picture) is uploaded and inserted at the caret.
        if (pictures.length && !text.trim() && !html.replace(/<img[^>]*>/gi, '').replace(/<[^>]+>/g, '').trim()) {
            this.pastePicture(pictures[0]);
            return;
        }
        if (this.mode === 'plain' || !html) {
            if (this.mode === 'rich' && text.includes('\n')) {
                const escaped = text.split(/\r?\n\s*\r?\n/).map(part => `<p>${part.replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c])).replace(/\r?\n/g, '<br>')}</p>`).join('');
                this.doc.execCommand('insertHTML', false, escaped);
            } else {
                this.doc.execCommand('insertText', false, this.mode === 'rich' ? text : text.replace(/\s+/g, ' '));
            }
            return;
        }
        this.doc.execCommand('insertHTML', false, cleanPastedHtml(html, this.mode, this.doc, { keepDataImages: !!this.editor.options.inlineImages }));
    }

    async pastePicture(file) {
        const media = this.editor.media;
        if (this.mode !== 'rich') return;
        if (!media.canUpload('image')) {
            this.editor.notify(this.editor.t('error.noUpload'), 'warning', 8000);
            return;
        }
        this.saveRange();
        const region = this.active;
        region.setAttribute('data-pf-uploading', '');
        const result = await media.upload(file, 'image');
        region.removeAttribute('data-pf-uploading');
        if (!result || this.active !== region) return;
        region.focus({ preventScroll: true });
        this.restoreRange();
        const img = this.doc.createElement('img');
        img.setAttribute('src', safeUrl(result.url, { image: true }));
        img.setAttribute('alt', result.alt || result.label || '');
        img.className = 'img-fluid';
        this.doc.execCommand('insertHTML', false, img.outerHTML);
        this.editor.changed();
    }

    closestIn(selector) {
        const sel = this.doc.getSelection();
        if (!sel || !sel.anchorNode || !this.active) return null;
        const node = sel.anchorNode.nodeType === 1 ? sel.anchorNode : sel.anchorNode.parentElement;
        const found = node && node.closest(selector);
        return found && this.active.contains(found) ? found : null;
    }

    /** Alignment as Bootstrap classes on the blocks of the selection. */
    align(className) {
        const blocks = this.selectedBlocks();
        if (!blocks.length && this.mode === 'rich' && this.active) blocks.push(this.active);
        const already = blocks.every(block => block.classList.contains(className));
        blocks.forEach(block => {
            block.classList.remove('text-start', 'text-center', 'text-end', 'text-justify');
            if (!already && className !== 'text-start') block.classList.add(className);
            if (!block.getAttribute('class')) block.removeAttribute('class');
        });
    }

    /** Block elements of the selection (or the block of the caret), inside the edited region. */
    selectedBlocks(selector = 'p, h2, h3, h4, h5, h6, li, blockquote, pre, figcaption, td, th') {
        const sel = this.doc.getSelection();
        if (!sel || !sel.rangeCount || !this.active) return [];
        const range = sel.getRangeAt(0);
        const blocks = new Set();
        const collect = node => {
            const el = node.nodeType === 1 ? node : node.parentElement;
            const block = el && el.closest(selector);
            if (block && this.active.contains(block)) blocks.add(block);
        };
        collect(range.startContainer);
        collect(range.endContainer);
        this.active.querySelectorAll(selector).forEach(node => {
            if (range.intersectsNode(node)) blocks.add(node);
        });
        return [...blocks];
    }

    /** Rows and columns of the table around the caret. */
    tableAction(action) {
        const cell = this.closestIn('td, th');
        if (!cell) return;
        const row = cell.parentElement;
        const table = cell.closest('table');
        const index = Array.from(row.children).indexOf(cell);
        const rows = Array.from(table.rows);
        const blank = tag => {
            const node = this.doc.createElement(tag);
            node.innerHTML = '<br>';
            return node;
        };
        const inHead = row.parentElement.localName === 'thead';
        if (action === 'rowAbove' || action === 'rowBelow') {
            const copy = this.doc.createElement('tr');
            Array.from(row.children).forEach(() => copy.append(blank(inHead && action === 'rowAbove' ? 'th' : 'td')));
            if (inHead && action === 'rowBelow') {
                const body = table.tBodies[0] || table.appendChild(this.doc.createElement('tbody'));
                body.prepend(copy);
            } else if (action === 'rowAbove') row.before(copy);
            else row.after(copy);
        } else if (action === 'colLeft' || action === 'colRight') {
            rows.forEach(tr => {
                const ref = tr.children[Math.min(index, tr.children.length - 1)];
                if (!ref) return;
                const node = blank(ref.localName);
                if (action === 'colLeft') ref.before(node);
                else ref.after(node);
            });
        } else if (action === 'deleteRow') {
            if (rows.length <= 1) table.remove();
            else row.remove();
        } else if (action === 'deleteCol') {
            if (row.children.length <= 1) table.remove();
            else rows.forEach(tr => tr.children[index] && tr.children[index].remove());
        } else if (action === 'deleteTable') {
            (table.parentElement.localName === 'figure' ? table.parentElement : table).remove();
        }
    }

    saveRange() {
        const sel = this.doc.getSelection();
        this.savedRange = sel && sel.rangeCount && this.active && this.active.contains(sel.anchorNode) ? sel.getRangeAt(0).cloneRange() : null;
    }

    restoreRange() {
        if (!this.savedRange) return;
        const sel = this.doc.getSelection();
        sel.removeAllRanges();
        sel.addRange(this.savedRange);
    }

    openLink() {
        if (!this.active) return;
        this.saveRange();
        const anchor = this.closestIn('a');
        this.linkInput.value = anchor ? anchor.getAttribute('href') || '' : '';
        this.linkNewTab.checked = anchor ? anchor.getAttribute('target') === '_blank' : false;
        this.linkInput.classList.remove('pf-invalid');
        const rect = this.toolbar.getBoundingClientRect();
        this.linkBox.style.display = 'flex';
        const view = this.doc.defaultView;
        const k = this.editor.uiScale();
        this.linkBox.style.transform = `translate(${Math.round(clamp(rect.left, 4, view.innerWidth - this.linkBox.offsetWidth * k - 4))}px, ${Math.round(rect.bottom + 6)}px) scale(${k})`;
        this.linkInput.focus();
        this.linkInput.select();
    }

    closeLink(refocus) {
        if (this.linkBox.style.display !== 'flex') return;
        this.linkBox.style.display = 'none';
        if (refocus && this.active) {
            this.active.focus({ preventScroll: true });
            this.restoreRange();
        }
    }

    applyLink() {
        const url = safeUrl(this.linkInput.value);
        if (!url) {
            this.linkInput.classList.add('pf-invalid');
            this.linkInput.focus();
            return;
        }
        this.closeLink(true);
        const existing = this.closestIn('a');
        const sel = this.doc.getSelection();
        if (existing && sel.isCollapsed) {
            existing.setAttribute('href', url);
        } else if (sel.isCollapsed) {
            const a = this.doc.createElement('a');
            a.setAttribute('href', url);
            a.textContent = url.replace(/^(?:https?:)?\/\//, '').replace(/^mailto:|^tel:/, '');
            this.doc.execCommand('insertHTML', false, a.outerHTML);
        } else {
            this.doc.execCommand('createLink', false, url);
        }
        // Target of the links just written.
        this.active.querySelectorAll('a').forEach(a => {
            if (a.getAttribute('href') !== url) return;
            if (this.linkNewTab.checked) {
                a.setAttribute('target', '_blank');
                a.setAttribute('rel', 'noopener');
            } else {
                a.removeAttribute('target');
                a.removeAttribute('rel');
            }
        });
        this.editor.changed();
    }

    removeLink() {
        this.closeLink(true);
        const anchor = this.closestIn('a');
        if (anchor && this.doc.getSelection().isCollapsed) {
            const range = this.doc.createRange();
            range.selectNodeContents(anchor);
            const sel = this.doc.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
        }
        this.doc.execCommand('unlink');
        this.editor.changed();
    }

    refreshToolbar() {
        if (!this.active || this.mode === 'plain' || this.adapter) {
            this.toolbar.style.display = 'none';
            return;
        }
        const doc = this.doc;
        this.toolbar.querySelectorAll('[data-command]').forEach(btn => {
            const command = btn.dataset.command;
            let on = false;
            try {
                if (['bold', 'italic', 'underline', 'strikeThrough', 'insertUnorderedList', 'insertOrderedList'].includes(command)) on = doc.queryCommandState(command);
            } catch (error) {
                on = false;
            }
            if (command === 'link') on = !!this.closestIn('a');
            if (command.startsWith('align-')) {
                const block = this.closestIn('p, h2, h3, h4, h5, h6, li, blockquote, pre');
                on = !!block && block.classList.contains(`text-${command.slice(6)}`);
            }
            btn.classList.toggle('pf-on', on);
            btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        if (this.mode === 'rich') {
            const block = this.closestIn('p, h2, h3, h4, h5, blockquote, pre');
            let value = block ? block.localName : 'p';
            if (block && value === 'p' && block.classList.contains('lead')) value = 'p.lead';
            else if (block && value === 'p' && block.classList.contains('small')) value = 'p.small';
            this.formatSelect.value = value;
            this.tableSelect.style.display = this.closestIn('td, th') ? '' : 'none';
        }
        this.toolbar.style.display = 'flex';
        this.position();
    }

    /** Above the edited block (where the block toolbar was), kept in view while the block is. */
    position() {
        if (!this.active || this.toolbar.style.display === 'none') return;
        const view = this.doc.defaultView;
        const block = this.editor.selected && this.editor.selected.contains(this.active) ? this.editor.selected : this.active;
        const rect = block.getBoundingClientRect();
        const k = this.editor.uiScale();
        const width = this.toolbar.offsetWidth * k;
        const height = this.toolbar.offsetHeight * k;
        const left = clamp(rect.left, 4, Math.max(4, view.innerWidth - width - 4));
        let top = rect.top - height - 6 * k;
        if (top < 4) top = clamp(4, rect.top + 4, Math.max(rect.top + 4, rect.bottom - height - 4));
        this.toolbar.style.transform = `translate(${Math.round(left)}px, ${Math.round(top)}px) scale(${k})`;
    }
}
